import type {
  HrSafetyReps,
  SafetyRepRegistration,
} from '../../../api/hrSafetyReps';

/*
 * Who may be registered, and for which teams, is checked in the API.
 * What is here fills the form, and checks it while it is filled in.
 */

type Representative = HrSafetyReps['representatives'][number];

export const NEW_PERSON = 'new';

export const teamNames = (rep: Representative, teams: HrSafetyReps['teams']) =>
  teams
    .filter((team) => rep.teamIds.includes(team.id))
    .map((team) => team.name);

/** The main safety representative acts for every team */
export const teamsWithoutSafetyRep = (data: HrSafetyReps) =>
  data.representatives.some((rep) => rep.isMain)
    ? []
    : data.teams.filter(
        (team) =>
          !data.representatives.some((rep) => rep.teamIds.includes(team.id)),
      );

export type SafetyRepForm = {
  person: string;
  email: string;
  teamIds: number[];
};

export type SafetyRepField = 'person' | 'email' | 'teams';

export const emptyForm = (teamId: number | null): SafetyRepForm => ({
  person: '',
  email: '',
  teamIds: teamId ? [teamId] : [],
});

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The same rules as the API, so the form says what is missing before it is sent */
export const formErrors = (
  form: SafetyRepForm,
  editing: boolean,
): SafetyRepField[] => [
  ...(!editing && !form.person ? ['person' as const] : []),
  ...(!editing && form.person === NEW_PERSON && !EMAIL.test(form.email.trim())
    ? ['email' as const]
    : []),
  ...(form.teamIds.length === 0 ? ['teams' as const] : []),
];

export const toRegistration = (form: SafetyRepForm): SafetyRepRegistration =>
  form.person === NEW_PERSON
    ? { email: form.email.trim(), teamIds: form.teamIds }
    : { userId: Number(form.person), teamIds: form.teamIds };

export const toggleTeam = (
  form: SafetyRepForm,
  teamId: number,
): SafetyRepForm => ({
  ...form,
  teamIds: form.teamIds.includes(teamId)
    ? form.teamIds.filter((id) => id !== teamId)
    : [...form.teamIds, teamId],
});

/** The people who can be chosen: everyone who is not the main safety representative */
export const candidates = (data: HrSafetyReps) =>
  data.users.filter(
    (candidate) =>
      !data.representatives.some(
        (rep) => rep.isMain && rep.userId === candidate.id,
      ),
  );
